import { Box, useToast, useColorModeValue } from "@chakra-ui/react";
import React from "react";
import { useHistory } from "react-router-dom";
import { Column } from "react-table";
import { QuoteApi } from "../../api/QuoteApi";
import { Quote } from "../../lib/models/quote.model";
import { DataTable, DataTableAction } from "../DataTable";
import DeleteQuoteDialog from "../DeleteQuoteDialog";
import QuotesTableToolbar from "./QuotesTableToolbar";

export interface QuotesTableProps {
  quotes: Quote[];
  loading: boolean;
  refetchQuotes: () => void;
  title: string;
  columns: Array<Column>;
}

function QuotesTable({
  quotes,
  loading,
  refetchQuotes,
  title,
  columns,
}: QuotesTableProps) {
  const toast = useToast();
  const history = useHistory();
  const [globalFilter, setGlobalFilter] = React.useState<string>("");
  const [isDeleteDialogOpen, setDeleteDialogOpen] =
    React.useState<boolean>(false);
  const [quoteToDelete, setQuoteToDelete] = React.useState<Quote | null>(null);
  const [isQuoteDeleting, setIsQuoteDeleting] = React.useState<boolean>(false);
  const toastVariant = useColorModeValue("subtle", "solid");

  const newQuoteHandler = () => {
    history.push("/quotes/new");
  };

  const handleTableAction = (quote: Quote, action: DataTableAction) => {
    if (action === "EDIT") {
      history.push(
        `/quotes/${quote.id}/edit?type=${quote.quoteType.toLowerCase()}`,
        { lastLocation: window.location.pathname }
      );
    }
    if (action === "DELETE") {
      setDeleteDialogOpen(true);
      setQuoteToDelete(quote);
    }
  };
  const onDeleteDialogConfirm = () => {
    if (!quoteToDelete) return;

    const id = quoteToDelete.id;

    setIsQuoteDeleting(true);

    QuoteApi.deactivateQuote(id)
      .then((res) => {
        toast({
          status: "success",
          position: "top-right",
          title: "Quote Deleted",
          description: "The quote has been successfully deleted",
          isClosable: true,
          variant: toastVariant,
        });
      })
      .catch((err) => {
        toast({
          status: "success",
          position: "top-right",
          title: "Unable to Delete Quote",
          description: "The quote could not be deleted",
          isClosable: true,
          variant: toastVariant,
        });
      })
      .finally(() => {
        setIsQuoteDeleting(false);
        setDeleteDialogOpen(false);
        refetchQuotes();
      });
  };

  const onDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
  };

  return (
    <>
      <DeleteQuoteDialog
        isOpen={isDeleteDialogOpen}
        isQuoteDeleting={isQuoteDeleting}
        onConfirmHandler={onDeleteDialogConfirm}
        onCloseHandler={onDeleteDialogClose}
        quoteToDelete={quoteToDelete}
      />
      <QuotesTableToolbar
        setGlobalFilter={setGlobalFilter}
        newQuoteHandler={newQuoteHandler}
        tableTitle={title}
      />
      <Box borderWidth="1px" marginTop="4">
        <DataTable
          loading={loading}
          handleAction={handleTableAction}
          globalFilter={globalFilter}
          columns={columns}
          data={quotes}
        />
      </Box>
    </>
  );
}

export default QuotesTable;
